import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { StreamingOverviewResponse } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { buildStreamingChartData, StreamingChartMetric, StreamingChartSplit } from '../buildStreamingChartData';
import { hasPortal, sectionSources } from '../streamingSources';
import { useStreamingDimension } from '../hooks/useStreamingDimension';
import { streamingTotalLabels } from '../streamingTotalsMessages';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { StreamingBlock } from './StreamingBlock';
import { StreamingChartControls } from './StreamingChartControls';
import { StreamingLineChart } from './StreamingLineChart';

interface StreamingChartBlockProps {
  accountId: number;
  overview: StreamingOverviewResponse;
  off: string[];
}

/** The chart with its number select and "Split by" (design b1). */
const StreamingChartBlockComponent: React.FC<StreamingChartBlockProps> = ({ accountId, overview, off }) => {
  const intl = useIntl();
  const [metric, setMetric] = useState<StreamingChartMetric>('streams');
  const [split, setSplit] = useState<StreamingChartSplit>('total');

  const metrics = useMemo(() => {
    const list: StreamingChartMetric[] = ['streams'];
    if (hasPortal(overview.sources)) {
      list.push('listeners', 'saves', 'playlistAdds');
    }
    if (overview.hasDistributor) {
      list.push('downloads');
    }
    return list;
  }, [overview]);

  // A split by store or country needs a source that reports the section: streams from any such
  // source (a direct portal is its own store), downloads only from a distributor.
  const splits = useMemo(() => {
    const allowed = (section: 'stores' | 'countries'): boolean => {
      const sources = sectionSources(overview, section);
      if (metric === 'streams') {
        return sources.length > 0;
      }
      if (metric === 'downloads') {
        return overview.sources.some(
          (source) => source.kind === 'distributor' && sources.includes(source.integrationType)
        );
      }
      return false;
    };
    return { store: allowed('stores'), country: allowed('countries') };
  }, [metric, overview]);
  const shownSplit: StreamingChartSplit =
    (split === 'store' && !splits.store) || (split === 'country' && !splits.country) ? 'total' : split;
  const dimension = shownSplit === 'store' || shownSplit === 'country' ? shownSplit : undefined;
  const dimensionRead = useStreamingDimension({
    accountId,
    dimension: dimension ?? 'store',
    days: overview.days,
    skip: dimension === undefined,
  });

  const data = useMemo(
    () =>
      buildStreamingChartData({
        series: overview.series,
        metric,
        split: shownSplit,
        hasDistributor: overview.hasDistributor,
        off,
        dimensionSeries: dimensionRead.data?.series,
      }),
    [dimensionRead.data, metric, off, overview, shownSplit]
  );

  const metricLabel = intl.formatMessage(streamingTotalLabels[metric]);
  return (
    <StreamingBlock
      title={intl.formatMessage(messages.chartTitle, { metric: metricLabel })}
      isLoading={dimension !== undefined && dimensionRead.isLoading}
      error={dimension !== undefined ? dimensionRead.error : undefined}
      retry={dimensionRead.retry}
      testId="streaming-chart"
    >
      <StreamingChartControls
        metrics={metrics}
        metric={metric}
        split={shownSplit}
        canSplitByStore={splits.store}
        canSplitByCountry={splits.country}
        onMetric={setMetric}
        onSplit={setSplit}
      />
      <StreamingLineChart data={data} totalLabel={metricLabel} dimensionIsCountry={shownSplit === 'country'} />
    </StreamingBlock>
  );
};

export const StreamingChartBlock = TypedMemo(StreamingChartBlockComponent);
